import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Building2, CreditCard, FileText, Car } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const { data } = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (data) setForm(data); }, [data]);
  if (!form) return null;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.updateSettings({ ...form, default_tva: Number(form.default_tva || 10) });
      toast.success("Paramètres enregistrés");
    } catch { toast.error("Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };

  const Section = ({ icon: Icon, title, children }) => (
    <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-5">
        <div className="h-9 w-9 rounded-lg bg-slate-900 flex items-center justify-center"><Icon className="h-4 w-4 text-[#C5A059]" /></div>
        <h3 className="font-display font-semibold text-slate-800">{title}</h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );

  const F = ({ label, k, ...props }) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`settings-${k}`} {...props} />
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Paramètres"
        subtitle="Informations de l'entreprise pour vos factures et devis"
        action={<Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="save-settings-btn">{saving ? "Enregistrement..." : "Enregistrer"}</Button>}
      />

      <div className="space-y-5 max-w-4xl">
        <Section icon={Building2} title="Identité de l'entreprise">
          <F label="Nom de l'entreprise" k="company_name" />
          <F label="Activité (sous-titre)" k="subtitle" />
          <F label="Adresse" k="address" />
          <F label="Code postal + Ville" k="postal_city" />
          <F label="SIRET" k="siret" />
          <F label="N° TVA intracom." k="tva_number" />
          <F label="Code APE / NAF" k="ape" />
          <div className="space-y-1.5">
            <Label>Taux de TVA par défaut (%)</Label>
            <Input type="number" value={form.default_tva ?? 10} onChange={(e) => set("default_tva", e.target.value)} data-testid="settings-default_tva" />
          </div>
          <F label="Téléphone" k="phone" />
          <F label="Email" k="email" />
        </Section>

        <Section icon={Car} title="Exploitant VTC & véhicule (bons de transport)">
          <F label="N° REVTC (registre exploitants VTC)" k="revtc" />
          <F label="Nom commercial (optionnel)" k="nom_commercial" />
          <F label="Véhicule — marque et modèle" k="vehicle_marque_modele" />
          <F label="Véhicule — immatriculation" k="vehicle_immatriculation" />
          <F label="Chauffeur — nom et prénom" k="chauffeur_nom" />
          <F label="Chauffeur — n° carte pro VTC" k="chauffeur_carte_pro" />
        </Section>

        <Section icon={CreditCard} title="Coordonnées bancaires (affichées sur les factures)">
          <F label="IBAN" k="iban" placeholder="FR76 ..." />
          <F label="BIC" k="bic" />
        </Section>

        <Section icon={FileText} title="Mentions légales">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Conditions de règlement</Label>
            <Textarea value={form.payment_terms || ""} onChange={(e) => set("payment_terms", e.target.value)} data-testid="settings-payment_terms" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Mentions de pied de page</Label>
            <Textarea value={form.legal_footer || ""} onChange={(e) => set("legal_footer", e.target.value)} data-testid="settings-legal_footer" rows={3} />
          </div>
        </Section>
      </div>
    </div>
  );
}
